import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, MenuItem, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useParams } from 'react-router-dom';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';
import KenButton from '../../../../../../GlobalComponents/KenButton';
import { getUserDetails } from '../../../../../../storageService';
import {
    fetchAssessmentFormDetail,
    addQuestion,
    addOption,
    AssessmentFormDetail,
} from '../../../Helper/AssessmentFormsHelper';
import routes from '../../../../../../Utils/routes';
import { enqueueSnackbar } from 'notistack';
import { invalidateFacultyActivitiesCache } from '../List/FacultyActivitiesData';

const useStyles = makeStyles((theme: any) => ({
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.KenColors.CttPrimary ?? '#2563EB',
        cursor: 'pointer',
        marginBottom: '16px',
    },
    card: {
        background: '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
    },
    fieldLabel: {
        fontSize: '13px',
        color: '#374151',
        marginBottom: '6px',
    },
    row: {
        display: 'flex',
        gap: '24px',
        marginBottom: '20px',
    },
    field: {
        flex: 1,
    },
    questionRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '16px 4px',
        borderBottom: '1px solid #F3F4F6',
    },
    questionIndex: {
        color: '#6B7280',
        fontWeight: 700,
        width: '22px',
        flex: 'none',
    },
    typePill: {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '13px',
        fontWeight: 500,
        display: 'inline-block',
        flex: 'none',
    },
    addQuestionSection: {
        borderTop: '1px solid #F3F4F6',
        marginTop: '16px',
        paddingTop: '18px',
    },
    sectionLabel: {
        fontSize: '12px',
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        marginBottom: '16px',
    },
}));

const answerTypeLabel = (type: string) => (type === 'Open Text' ? 'Open text' : 'Multiple choice');

interface DraftQuestion {
    text: string;
    answerType: 'Multiple Choice' | 'Open Text';
    options: string[];
}

const emptyDraft = (): DraftQuestion => ({ text: '', answerType: 'Multiple Choice', options: [] });

const AssessmentView: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState<AssessmentFormDetail | null>(null);
    const [draft, setDraft] = useState<DraftQuestion>(emptyDraft());
    const [saving, setSaving] = useState(false);

    const contactId = getUserDetails()?.ContactId;

    const load = async () => {
        if (!id || !contactId) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await fetchAssessmentFormDetail(id, contactId, 'Faculty');
            setForm(data);
        } catch (error) {
            console.error('Error fetching assessment form:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // New questions are queued locally and only sent to the backend when
    // "Save form" is clicked — matches the New form page's create flow.
    const [pendingQuestions, setPendingQuestions] = useState<DraftQuestion[]>([]);

    const addQuestionToDraft = () => {
        if (!draft.text.trim()) {
            enqueueSnackbar('Type the question first.', { variant: 'warning' });
            return;
        }
        if (draft.answerType === 'Multiple Choice' && draft.options.filter((o) => o.trim()).length < 2) {
            enqueueSnackbar('Multiple choice needs at least two options (one per line).', { variant: 'warning' });
            return;
        }
        setPendingQuestions((prev) => [...prev, draft]);
        setDraft(emptyDraft());
    };

    const removePendingQuestion = (index: number) => {
        setPendingQuestions((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveForm = async () => {
        if (!id || !contactId) return;
        if (pendingQuestions.length === 0) {
            enqueueSnackbar('Add a question before saving.', { variant: 'warning' });
            return;
        }
        try {
            setSaving(true);
            const startOrder = (form?.questions.length ?? 0) + 1;
            for (let i = 0; i < pendingQuestions.length; i++) {
                const q = pendingQuestions[i];
                const questionRes = await addQuestion({
                    contactId,
                    formId: id,
                    questionText: q.text,
                    answerType: q.answerType,
                    order: startOrder + i,
                    isRequired: true,
                });
                const questionId = questionRes?.recordId || questionRes?.RecordId;
                if (q.answerType === 'Multiple Choice' && questionId) {
                    const optionLines = q.options.filter((o) => o.trim());
                    for (let j = 0; j < optionLines.length; j++) {
                        await addOption({ contactId, questionId, optionText: optionLines[j], order: j + 1 });
                    }
                }
            }
            setPendingQuestions([]);
            invalidateFacultyActivitiesCache(contactId);
            enqueueSnackbar('Form saved successfully', { variant: 'success' });
            await load();
        } catch (error) {
            console.error('Error saving form:', error);
            enqueueSnackbar('Unable to save this form right now.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <KenLoader />;

    const existingCount = form?.questions.length ?? 0;
    const totalCount = existingCount + pendingQuestions.length;

    return (
        <Box>
            <Typography className={classes.backLink} onClick={() => navigate(routes.AssessmentAdding)}>
                <ArrowBackIcon fontSize="small" /> Back to activities
            </Typography>

            <Box className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Edit form</Typography>
                    <KenButton
                        variant="contained"
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                        disabled={saving || pendingQuestions.length === 0}
                        onClick={handleSaveForm}
                    >
                        {saving ? 'Saving…' : 'Save form'}
                    </KenButton>
                </Box>

                <Box className={classes.row}>
                    <Box className={classes.field}>
                        <Typography className={classes.fieldLabel}>Form type</Typography>
                        <TextField fullWidth value={form?.formType || ''} disabled />
                    </Box>
                    <Box className={classes.field}>
                        <Typography className={classes.fieldLabel}>Form title</Typography>
                        <TextField fullWidth value={form?.title || ''} disabled />
                    </Box>
                </Box>
            </Box>

            <Box className={classes.card}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
                    Questions{totalCount > 0 ? ` · ${totalCount}` : ''}
                </Typography>

                {(form?.questions ?? []).map((question, index) => (
                    <Box key={question.questionId} className={classes.questionRow}>
                        <Typography className={classes.questionIndex}>{index + 1}.</Typography>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px' }}>{question.questionText}</Typography>
                            {question.answerType !== 'Open Text' && question.options.length > 0 && (
                                <Typography sx={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                                    {question.options.map((o) => o.optionText).join('   ')}
                                </Typography>
                            )}
                            {question.answerType === 'Open Text' && (
                                <Typography sx={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                                    Open-text answer
                                </Typography>
                            )}
                        </Box>
                        <span className={classes.typePill} style={{ background: '#E9F2FB', color: '#2380C4' }}>
                            {answerTypeLabel(question.answerType)}
                        </span>
                        {/* Deleting an existing question isn't supported by the
                            AssessmentForms API yet (no delete/removeQuestion
                            action documented) — disabled rather than faked. */}
                        <IconButton size="small" disabled title="Deleting questions isn't supported yet">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}

                {pendingQuestions.map((q, index) => (
                    <Box key={`pending-${index}`} className={classes.questionRow}>
                        <Typography className={classes.questionIndex}>{existingCount + index + 1}.</Typography>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '14px' }}>{q.text}</Typography>
                            {q.answerType === 'Multiple Choice' && (
                                <Typography sx={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                                    {q.options.filter(Boolean).join('   ')}
                                </Typography>
                            )}
                        </Box>
                        <span className={classes.typePill} style={{ background: '#FBF3DE', color: '#CE9526' }}>
                            {q.answerType === 'Multiple Choice' ? 'Multiple choice' : 'Open text'} · not saved
                        </span>
                        <IconButton size="small" onClick={() => removePendingQuestion(index)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}

                {totalCount === 0 && (
                    <Typography sx={{ color: '#6B7280', fontSize: '14px', padding: '16px 4px' }}>
                        No questions added to this form yet.
                    </Typography>
                )}

                <Box className={classes.addQuestionSection}>
                    <Typography className={classes.sectionLabel}>Add a question</Typography>
                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography className={classes.fieldLabel}>Question</Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="Type the question..."
                            value={draft.text}
                            onChange={(e) => setDraft((prev) => ({ ...prev, text: e.target.value }))}
                        />
                    </Box>
                    <Box sx={{ marginBottom: '16px' }}>
                        <Typography className={classes.fieldLabel}>Answer type</Typography>
                        <TextField
                            select
                            fullWidth
                            value={draft.answerType}
                            onChange={(e) => setDraft((prev) => ({ ...prev, answerType: e.target.value as 'Multiple Choice' | 'Open Text' }))}
                        >
                            <MenuItem value="Multiple Choice">Multiple choice</MenuItem>
                            <MenuItem value="Open Text">Open text</MenuItem>
                        </TextField>
                    </Box>
                    {draft.answerType === 'Multiple Choice' && (
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography className={classes.fieldLabel}>Options — one per line (multiple choice only)</Typography>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                placeholder={'Option A\nOption B\nOption C'}
                                value={draft.options.join('\n')}
                                onChange={(e) => setDraft((prev) => ({ ...prev, options: e.target.value.split('\n') }))}
                            />
                        </Box>
                    )}
                    <KenButton variant="contained" sx={{ textTransform: 'none', borderRadius: '8px' }} onClick={addQuestionToDraft}>
                        Add question
                    </KenButton>
                </Box>
            </Box>
        </Box>
    );
};

export default AssessmentView;
